import React, { useEffect, useState } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { PlusIcon } from "@heroicons/react/16/solid";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { Input } from "../design/input";
import { Textarea } from "../design/textarea";
import { Select } from "../design/select";
import { Checkbox, CheckboxField } from "../design/checkbox";
import AssignToField from "../components/AssignToField";
import { useTask } from "../contexts/TaskContext";
import { Task } from "../models/Task";
import { DiscoveryRequest } from "../models/DiscoveryRequest";
import { User } from "../models/User";
import SelectUsersField from "./Fields/SelectUsersField";

interface Props {
  task?: Task;
  discoveryRequest?: DiscoveryRequest["_id"];
  onTaskCreated?: (taskId: string) => void;
  children?: React.ReactNode;
}

interface Errors {
  title?: string;
  description?: string;
}

const TaskButton: React.FC<Props> = ({
  task,
  discoveryRequest,
  onTaskCreated,
  children,
}) => {
  const isEditMode = !!task;
  const { createTask, updateTask } = useTask();

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingMore, setIsCreatingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState<string>(task?.title ?? "");
  const [description, setDescription] = useState<string>(
    task?.description ?? "",
  );
  const [type, setType] = useState<Task["type"]>(task?.type ?? "general");
  const [assignedTo, setAssignedTo] = useState<string[]>(
    (task?.assignedTo as User["_id"][]) ?? [],
  );
  const [useAI, setUseAI] = useState(localStorage.getItem("useAI") === "true");
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (isEditMode && task) {
      setTitle(task.title);
      setDescription(task.description ?? "");
      setType(task.type);
      setAssignedTo((task.assignedTo as User["_id"][]) ?? []);
    }
  }, [isOpen, task]);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!title.trim()) newErrors.title = "A title is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    setError(null);

    try {
      if (isEditMode && task) {
        await updateTask(task._id, { title, description, assignedTo });
      } else {
        const taskId = await createTask({
          title,
          description,
          type: discoveryRequest ? "discovery" : type,
          isCompleted: false,
          discoveryRequest,
          assignedTo,
          useAI,
        });
        if (onTaskCreated) onTaskCreated(taskId);
        if (!isCreatingMore) setIsOpen(false);
        resetForm();
      }
    } catch (e) {
      console.error("Error submitting task:", e);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setType("general");
    setAssignedTo([]);
    setErrors({});
    setError(null);
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        {children ?? (
          <div className="flex items-center gap-2">
            {!isEditMode && <PlusIcon className="w-4 h-4" />}
            <span className="hidden sm:block">
              {isEditMode ? "Edit Task" : "Create Task"}
            </span>
          </div>
        )}
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>{isEditMode ? "Edit Task" : "Create Task"}</DialogTitle>
        <DialogBody>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Title</Label>
                <Input
                  type="text"
                  value={title}
                  placeholder={
                    type === "discovery"
                      ? "e.g. Request bank statements"
                      : "e.g. Document exchange"
                  }
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isSaving}
                />
                {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
              </Field>

              {!discoveryRequest && !isEditMode && (
                <Field>
                  <Label>Type</Label>
                  <Select
                    value={type}
                    onChange={(e) => setType(e.target.value as Task["type"])}
                    disabled={isSaving}
                  >
                    <option value="general">General</option>
                    <option value="document">Document</option>
                    <option value="event">Event</option>
                    <option value="evidence">Files</option>
                  </Select>
                </Field>
              )}

              <SelectUsersField
                title="Assign to"
                values={assignedTo}
                onChange={setAssignedTo}
              />

              <AssignToField value={assignedTo} onChange={setAssignedTo} />

              {(discoveryRequest || isEditMode) && (
                <>
                  <Field>
                    <Label>Description</Label>
                    <Textarea
                      value={description}
                      placeholder="What happened?"
                      onChange={(e) => setDescription(e.target.value)}
                      disabled={isSaving}
                    />
                    {errors.description && (
                      <ErrorMessage>{errors.description}</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>How do you want to create discovery subtasks?</Label>
                    <div className="mt-2 space-y-2">
                      <button
                        type="button"
                        onClick={() => {
                          setUseAI(true);
                          localStorage.setItem("useAI", "true");
                        }}
                        className={`w-full text-left border rounded-lg p-4 ${
                          useAI
                            ? "border-blue-500 bg-blue-50 dark:bg-zinc-800"
                            : "border-zinc-300 dark:border-zinc-700"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-zinc-800 dark:text-zinc-100">
                            Use AI
                          </span>
                          {useAI && (
                            <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                              Selected
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                          Let AI draft subtasks based on the description above.
                        </p>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setUseAI(false);
                          localStorage.setItem("useAI", "false");
                        }}
                        className={`w-full text-left border rounded-lg p-4 ${
                          !useAI
                            ? "border-blue-500 bg-blue-50 dark:bg-zinc-800"
                            : "border-zinc-300 dark:border-zinc-700"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-zinc-800 dark:text-zinc-100">
                            Manual
                          </span>
                          {!useAI && (
                            <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                              Selected
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                          You'll write out subtasks manually based on the
                          description.
                        </p>
                      </button>
                    </div>
                  </Field>
                </>
              )}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions className="justify-between">
          {!isEditMode && (
            <CheckboxField>
              <Checkbox
                checked={isCreatingMore}
                onChange={(checked) => setIsCreatingMore(checked)}
              />
              <Label>Create more</Label>
            </CheckboxField>
          )}
          <div className="flex gap-2">
            <Button plain onClick={() => setIsOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button disabled={isSaving} onClick={handleSubmit}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TaskButton;
